import { Card, CardContent } from "@/components/ui/card";
import { ClickableAddress } from "@/components/ui/clickable-address";
import { PriceBadge } from "@/components/ui/price-badge";
import { ProgressiveImage } from "@/components/ui/progressive-image";
import { storage } from "@/lib/storage";
import type { Studio } from "@/types/database";
import { MapPin, Music } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface StudioCardProps {
  studio: Studio;
  lowestPrice?: number | null;
  showProgressiveImage?: boolean;
  showClickableAddress?: boolean;
  /** Compact mode: shorter aspect ratio, no bio. Used on landing page. */
  compact?: boolean;
}

// Extract city from a full address string
// Assumes format like "123 Main St, City, State ZIP" or "City, State"
function extractCity(location: string): string {
  const parts = location.split(",").map((part) => part.trim());
  if (parts.length >= 2) {
    // For addresses like "123 Main St, City, State ZIP", city is the second part
    // For addresses like "City, State", city is the first part
    // Heuristic: if first part looks like a street address (contains numbers), use second part
    const hasStreetNumber = /^\d/.test(parts[0]);
    return hasStreetNumber && parts.length >= 2 ? parts[1] : parts[0];
  }
  return location; // Return as-is if we can't parse it
}

export function StudioCard({
  studio,
  lowestPrice,
  showProgressiveImage = false,
  showClickableAddress = false,
  compact = false,
}: StudioCardProps) {
  const navigate = useNavigate();

  const handleCardClick = () => {
    // Use handle for URL if available, otherwise use ID
    const path = studio.handle ? `/${studio.handle}` : `/studios/${studio.id}`;
    navigate(path);
  };

  return (
    <Card
      data-testid="studio-card"
      className="group overflow-hidden hover:shadow-xl transition-shadow duration-300 p-0 bg-card h-full flex flex-col cursor-pointer outline-none ring-0"
      onClick={handleCardClick}
    >
      <CardContent className="p-0 flex flex-col h-full">
        <div className="relative h-full">
          {/* Studio Photo - full card background */}
          {studio.photos && studio.photos.length > 0 ? (
            <div className={`overflow-hidden ${compact ? "aspect-video" : "aspect-[4/5]"}`} style={{ touchAction: 'manipulation' }}>
              {showProgressiveImage ? (
                <ProgressiveImage
                  src={studio.photos[0]}
                  thumbnailSrc={storage.getThumbnailUrl(studio.photos[0], {
                    width: 280,
                    height: 350,
                    quality: 30,
                  })}
                  alt={studio.name}
                  className="w-full h-full transition-transform duration-300 group-hover:scale-105"
                  aspectRatio="auto"
                />
              ) : (
                <img
                  src={studio.photos[0]}
                  alt={studio.name}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  draggable={false}
                />
              )}
            </div>
          ) : (
            <div className={`relative w-full ${compact ? "aspect-video" : "aspect-[4/5]"} bg-muted flex items-center justify-center`}>
              <div className="text-center text-on-dark-muted">
                <div className="h-16 w-16 rounded-full bg-on-dark-muted/10 flex items-center justify-center mx-auto mb-2">
                  <Music className="h-8 w-8" />
                </div>
                <p className="text-sm text-on-dark-muted">No photo available</p>
              </div>
            </div>
          )}

          {/* Overlay for text visibility */}
          {compact && <div className="absolute inset-0 bg-black/50 pointer-events-none" />}
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent pointer-events-none" />

          {/* Price badge - top right */}
          {lowestPrice && (
            <div className="absolute top-3 right-3 sm:top-4 sm:right-4 z-20">
              <PriceBadge
                price={lowestPrice}
                className="px-2.5 py-1 sm:px-3"
                showFrom={true}
                showPerHour={true}
              />
            </div>
          )}

          {/* Studio info - overlaid on bottom (or centered in compact) */}
          <div className={`absolute inset-x-0 p-4 sm:p-6 space-y-2 z-10 ${compact ? "inset-y-0 flex flex-col items-center justify-center text-center" : "bottom-0"}`}>
            {/* Studio name - large text */}
            <h3 className={`font-bold tracking-tight text-white ${compact ? "text-xl sm:text-2xl" : "text-2xl sm:text-3xl"}`}>
              {studio.name}
            </h3>

            {/* Location - show just the city */}
            {studio.location && (
              <div className={`flex items-center gap-1.5 ${compact ? "justify-center" : ""}`} onClick={(e) => showClickableAddress && e.stopPropagation()}>
                <MapPin className="h-4 w-4 flex-shrink-0 text-on-dark/80" />
                {showClickableAddress ? (
                  <ClickableAddress
                    address={studio.location}
                    displayText={extractCity(studio.location)}
                    className="text-sm text-on-dark-muted hover:text-on-dark truncate"
                    showIcon={false}
                    truncate={true}
                  />
                ) : (
                  <p className="text-sm text-on-dark-muted truncate">{extractCity(studio.location)}</p>
                )}
              </div>
            )}

            {/* Description — hidden in compact mode */}
            {!compact && studio.bio && (
              <p className="text-sm text-on-dark-muted line-clamp-3 overflow-hidden whitespace-normal">
                {studio.bio}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
